import React, { useState } from 'react';
import { 
  Container, 
  Paper, 
  TextField, 
  Button, 
  Typography, 
  Box, 
  Alert,
  Tab,
  Tabs,
  Fade,
  Grow,
  InputAdornment
} from '@mui/material';
import { Email, Lock, SelfImprovement } from '@mui/icons-material';
import { useAuth } from '../contexts/AuthContext';
import { IconAvatar } from './IconSystem';

function TabPanel({ children, value, index }) {
  return (
    <div hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

export default function Login() {
  const [tabValue, setTabValue] = useState(0);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, signup } = useAuth();

  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
    setError('');
  };

  async function handleLogin(e) {
    e.preventDefault();
    
    try {
      setError('');
      setLoading(true);
      await login(email, password);
    } catch (error) {
      setError('ログインに失敗しました: ' + error.message);
    }
    setLoading(false);
  }

  async function handleSignup(e) {
    e.preventDefault();

    if (password !== confirmPassword) {
      return setError('パスワードが一致しません');
    }

    try {
      setError('');
      setLoading(true);
      await signup(email, password);
    } catch (error) {
      setError('アカウント作成に失敗しました: ' + error.message);
    }
    setLoading(false);
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      background: 'background.default',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      p: 2,
      position: 'relative',
      '&::before': {
        content: '""',
        position: 'absolute',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'background.default',
        zIndex: -1,
      }
    }}>
      <Container component="main" maxWidth="sm">
        <Fade in timeout={800}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
            }}
          >
            <Grow in timeout={1000}>
              <Paper 
                sx={{ 
                  width: '100%', 
                  background: 'rgba(255, 255, 255, 0.1)',
                  backdropFilter: 'blur(20px)',
                  border: '1px solid rgba(255, 255, 255, 0.2)',
                  borderRadius: '24px',
                  overflow: 'hidden',
                }}
              >
                <Box sx={{ p: 4, textAlign: 'center' }}>
                  <SelfImprovement sx={{ fontSize: '4rem', color: 'primary.main', mb: 2 }} />
                  <Typography component="h1" variant="h4" sx={{ fontWeight: 700, mb: 3 }}>
                    セルフケア記録
                  </Typography>
                </Box>
                
                <Tabs 
                  value={tabValue} 
                  onChange={handleTabChange} 
                  centered
                  sx={{
                    '& .MuiTab-root': {
                      fontWeight: 600,
                      fontSize: '1rem',
                      borderRadius: '12px 12px 0 0',
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        backgroundColor: 'rgba(255, 255, 255, 0.1)',
                        transform: 'translateY(-2px)',
                      },
                    },
                    '& .MuiTabs-indicator': {
                      height: '3px',
                      borderRadius: '2px',
                      background: 'linear-gradient(90deg, #66BB6A, #42A5F5)',
                    },
                  }}
                >
                  <Tab label="ログイン" />
                  <Tab label="新規登録" />
                </Tabs>

                {error && (
                  <Fade in>
                    <Alert 
                      severity="error" 
                      sx={{ 
                        m: 3, 
                        borderRadius: '16px',
                        background: 'rgba(244, 67, 54, 0.1)',
                        backdropFilter: 'blur(10px)',
                        border: '1px solid rgba(244, 67, 54, 0.3)',
                      }}
                    >
                      {error}
                    </Alert>
                  </Fade>
                )}

                <TabPanel value={tabValue} index={0}>
                  <Box component="form" onSubmit={handleLogin} sx={{ p: 4 }}>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="email"
                      label="メールアドレス"
                      name="email"
                      autoComplete="email"
                      autoFocus
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email color="primary" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      name="password"
                      label="パスワード"
                      type="password"
                      id="password"
                      autoComplete="current-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock color="primary" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 3 }}
                    />
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loading}
                      size="large"
                      sx={{ 
                        mt: 2, 
                        mb: 3,
                        py: 2,
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, #66BB6A, #42A5F5)',
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        boxShadow: '0 8px 24px rgba(102, 187, 106, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #4CAF50, #1976D2)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 12px 32px rgba(102, 187, 106, 0.4)',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      ログイン
                    </Button>
                  </Box>
                </TabPanel>

                <TabPanel value={tabValue} index={1}>
                  <Box component="form" onSubmit={handleSignup} sx={{ p: 4 }}>
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      id="signup-email"
                      label="メールアドレス"
                      name="email"
                      autoComplete="email"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Email color="primary" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      name="password"
                      label="パスワード"
                      type="password"
                      id="signup-password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock color="primary" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 2 }}
                    />
                    <TextField
                      margin="normal"
                      required
                      fullWidth
                      name="confirmPassword"
                      label="パスワード確認"
                      type="password"
                      id="confirm-password"
                      value={confirmPassword}
                      onChange={(e) => setConfirmPassword(e.target.value)}
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <Lock color="primary" />
                          </InputAdornment>
                        ),
                      }}
                      sx={{ mb: 3 }}
                    />
                    <Button
                      type="submit"
                      fullWidth
                      variant="contained"
                      disabled={loading}
                      size="large"
                      sx={{ 
                        mt: 2, 
                        mb: 3,
                        py: 2,
                        borderRadius: '16px',
                        background: 'linear-gradient(135deg, #42A5F5, #EC407A)',
                        fontSize: '1.1rem',
                        fontWeight: 700,
                        boxShadow: '0 8px 24px rgba(66, 165, 245, 0.3)',
                        '&:hover': {
                          background: 'linear-gradient(135deg, #1976D2, #C2185B)',
                          transform: 'translateY(-2px)',
                          boxShadow: '0 12px 32px rgba(66, 165, 245, 0.4)',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      アカウント作成
                    </Button>
                  </Box>
                </TabPanel>
              </Paper>
            </Grow>
          </Box>
        </Fade>
      </Container>
    </Box>
  );
}